/**
 * `3b` — Questionário do padrão. A tela central do módulo, escopada por cliente.
 *
 * 🔒 **Os totais vêm do servidor** (`GET …/questionario`). A tela não soma nota:
 * a fórmula do §3 é do backend, e uma resposta trocada invalida a consulta.
 *
 * 🔒 **Nada bloqueia o preenchimento.** A divergência IA × resposta pinta a linha de
 * vermelho e mais nada — a submissão oficial acontece em outro sistema.
 *
 * ⚠️ O aviso fixo de rodapé do shot `3b` ("A plataforma nunca bloqueia o
 * preenchimento…") saiu de propósito — Fábio, 15/10/2026. Não recolocar a partir
 * do handoff.
 *
 * 🔒 BR-22: o veredito da IA é o único parecer. "Nota para IA" é o retorno do
 * arquiteto IOX SOBRE esse parecer (`compliance.notas_ia`) — o Cliente só lê.
 *
 * Sugestão da IA na linha (Eduardo, via Fábio, 23/10/2026): uma sugestão pendente
 * aparece na coluna Evidências, e a coluna Veredito da IA ganha aceitar / recusar.
 * Aceitar cria o vínculo e o backend pede o parecer (PR #100 do backend) — até ele
 * chegar, a coluna diz "aguardando parecer da IA". O "×" ao lado de cada evidência
 * desvincula o documento DESTA pergunta; se era o único requisito do documento, a
 * linha pede confirmação para excluir o documento (o backend não aceita evidência
 * sem vínculo). Tudo invalida questionário, documentos e sugestões juntos.
 */
import { useState } from 'react'
import { Link } from 'react-router'

import type { Evidencia, Grupo, ItemQuestionario, Pergunta, Sugestao } from '@/api/contracts/compliance'
import {
  obterUrlDeDownload,
  useAtualizarVinculos,
  useDecidirSugestao,
  useEvidencias,
  useExcluirEvidencia,
  useEstrutura,
  useQuestionario,
  useSalvarNotaParaIA,
  useSalvarResposta,
  useSugestoes,
} from '@/api/queries/compliance'
import { mensagemDoErro } from '@/components/erros'
import { EsqueletoDeCards, EsqueletoDeTabela, RegiaoDeCarregamento } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { Badge, Botao, MetricCard } from '@/components/primitivos'
import { formatarDecimal, formatarPercentual } from '@/format'
import { usePermission } from '@/permissions/usePermission'
import { useClienteAtual } from '@/tenant/useClienteAtual'

import { usePadraoSelecionado } from '../padrao'
import { gruposEmOrdem, rotuloCurtoDoGrupo, rotuloDoGrupo } from '../rotulos'

type Filtro = { tipo: 'todas' } | { tipo: 'grupo'; id: string } | { tipo: 'sem-evidencia' } | { tipo: 'divergencia' }

const VEREDITO: Record<string, { rotulo: string; tom: 'sucesso' | 'neutro' | 'perigo' | 'atencao' }> = {
  aderente: { rotulo: 'aderente', tom: 'sucesso' },
  concorda: { rotulo: 'concorda', tom: 'neutro' },
  discorda: { rotulo: 'discorda', tom: 'perigo' },
  'sem-evidencia': { rotulo: 'sem evidência', tom: 'atencao' },
}

const NOTAS = Array.from({ length: 10 }, (_, i) => i + 1)

export function Questionario() {
  const idCliente = useClienteAtual()
  const { consulta: padroes, padrao } = usePadraoSelecionado()
  const estrutura = useEstrutura(padrao?.id)
  const questionario = useQuestionario(idCliente, padrao?.id)
  const sugestoes = useSugestoes(idCliente, padrao?.id)
  // O vínculo completo de cada documento (o item do questionário só traz id/nome/tipo):
  // é o que o "×" precisa para desvincular só ESTA pergunta.
  const evidencias = useEvidencias(idCliente, '')

  if (padroes.isError) return <EstadoDeErro erro={padroes.error} aoTentarNovamente={() => void padroes.refetch()} />
  if (!padroes.isPending && !padrao) {
    return <EstadoVazio titulo="Nenhum padrão de conformidade cadastrado." descricao="A IOX cadastra o padrão do ano antes do preenchimento." />
  }
  if (estrutura.isError) return <EstadoDeErro erro={estrutura.error} aoTentarNovamente={() => void estrutura.refetch()} />
  if (questionario.isError) {
    return <EstadoDeErro erro={questionario.error} aoTentarNovamente={() => void questionario.refetch()} />
  }
  if (!padrao || estrutura.isPending || questionario.isPending) {
    return (
      <RegiaoDeCarregamento rotulo="Carregando questionário…" className="flex flex-col gap-5">
        <EsqueletoDeCards quantidade={4} />
        <EsqueletoDeTabela linhas={6} colunas={7} />
      </RegiaoDeCarregamento>
    )
  }

  const pendentes = (sugestoes.data ?? []).filter((s) => s.estado === 'pendente')

  return (
    <TabelaDoQuestionario
      key={`${idCliente}-${padrao.id}`}
      idCliente={idCliente}
      padraoId={padrao.id}
      padraoNome={padrao.nome}
      grupos={estrutura.data.grupos}
      perguntas={estrutura.data.perguntas}
      itens={questionario.data.itens}
      totais={questionario.data.totais}
      sugestoesPendentes={pendentes}
      evidencias={evidencias.data ?? []}
    />
  )
}

function TabelaDoQuestionario({
  idCliente,
  padraoId,
  padraoNome,
  grupos,
  perguntas,
  itens,
  totais,
  sugestoesPendentes,
  evidencias,
}: {
  idCliente: string
  padraoId: string
  padraoNome: string
  grupos: Grupo[]
  perguntas: Pergunta[]
  itens: ItemQuestionario[]
  totais: { nota: number; notaMaxima: number; percentual: number; respondidas: number; total: number; comEvidencia: number; divergencias: number }
  sugestoesPendentes: Sugestao[]
  evidencias: Evidencia[]
}) {
  const [filtro, setFiltro] = useState<Filtro>({ tipo: 'todas' })
  const porPergunta = new Map(itens.map((i) => [i.perguntaId, i]))
  const ordenados = gruposEmOrdem(grupos)
  const folhas = ordenados.filter((g) => perguntas.some((p) => p.grupoId === g.id))
  const semEvidencia = perguntas.filter((p) => (porPergunta.get(p.id)?.evidencias.length ?? 0) === 0).length
  const divergentes = perguntas.filter((p) => porPergunta.get(p.id)?.veredito?.situacao === 'discorda').length

  function passa(p: Pergunta): boolean {
    const item = porPergunta.get(p.id)
    switch (filtro.tipo) {
      case 'todas':
        return true
      case 'grupo':
        return p.grupoId === filtro.id
      case 'sem-evidencia':
        return (item?.evidencias.length ?? 0) === 0
      case 'divergencia':
        return item?.veredito?.situacao === 'discorda'
    }
  }

  const blocos = folhas
    .map((g) => ({
      grupo: g,
      perguntas: perguntas.filter((p) => p.grupoId === g.id && passa(p)).sort((a, b) => a.ordem - b.ordem),
    }))
    .filter((b) => b.perguntas.length > 0)

  return (
    <section className="flex flex-col gap-5 pb-4">
      <div className="rounded-card border-navy-200 bg-navy-50 flex flex-wrap items-center gap-4 border p-4">
        <i className="ph ph-sparkle text-iox-teal text-2xl" aria-hidden="true" />
        <p className="type-body text-iox-navy flex-1">
          {sugestoesPendentes.length > 0 ? (
            <>
              <strong>
                {sugestoesPendentes.length}{' '}
                {sugestoesPendentes.length === 1 ? 'sugestão' : 'sugestões'} de reaproveitamento
              </strong>{' '}
              de evidências para o padrão {padraoNome}.
            </>
          ) : (
            <>Nenhuma sugestão de evidência pendente para o padrão {padraoNome}.</>
          )}
        </p>
        <Link
          to={`/cliente/${idCliente}/compliance/novo-padrao?padrao=${encodeURIComponent(padraoId)}`}
          className="rounded-control border-border-subtle bg-surface-card text-text-primary hover:text-iox-teal inline-flex items-center gap-2 border px-4 py-2 type-button"
        >
          <i className="ph ph-sparkle" aria-hidden="true" />
          sugestões de evidências
        </Link>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          rotulo="Nota alcançada no padrão"
          valor={formatarDecimal(totais.nota)}
          caption={`de ${formatarDecimal(totais.notaMaxima)} possíveis · ${formatarPercentual(totais.percentual)}`}
          grande
        />
        <MetricCard rotulo="Perguntas respondidas" valor={`${totais.respondidas} / ${totais.total}`} grande />
        <MetricCard
          rotulo="Com evidência comprobatória"
          valor={`${totais.comEvidencia} / ${totais.total}`}
          caption={`${totais.total - totais.comEvidencia} ${totais.total - totais.comEvidencia === 1 ? 'pergunta' : 'perguntas'} sem documento`}
          grande
        />
        <MetricCard
          rotulo="Divergências com a IA"
          valor={String(totais.divergencias)}
          caption="revisar antes de consolidar"
          negativo={totais.divergencias > 0}
          grande
        />
      </div>

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtros do questionário">
        <ChipDeFiltro ativo={filtro.tipo === 'todas'} aoClicar={() => setFiltro({ tipo: 'todas' })}>
          Todas ({perguntas.length})
        </ChipDeFiltro>
        {folhas.map((g) => (
          <ChipDeFiltro
            key={g.id}
            ativo={filtro.tipo === 'grupo' && filtro.id === g.id}
            aoClicar={() => setFiltro({ tipo: 'grupo', id: g.id })}
          >
            {rotuloCurtoDoGrupo(g)} ({perguntas.filter((p) => p.grupoId === g.id).length})
          </ChipDeFiltro>
        ))}
        <span className="ml-auto flex gap-2">
          <ChipDeFiltro tom="atencao" ativo={filtro.tipo === 'sem-evidencia'} aoClicar={() => setFiltro({ tipo: 'sem-evidencia' })}>
            <i className="ph ph-paperclip" aria-hidden="true" /> Sem evidência ({semEvidencia})
          </ChipDeFiltro>
          <ChipDeFiltro tom="perigo" ativo={filtro.tipo === 'divergencia'} aoClicar={() => setFiltro({ tipo: 'divergencia' })}>
            <i className="ph ph-warning" aria-hidden="true" /> Divergência da IA ({divergentes})
          </ChipDeFiltro>
        </span>
      </div>

      <div className="border-border-subtle bg-surface-card rounded-card overflow-x-auto border">
        <table className="w-full min-w-[1100px] text-left">
          <thead className="bg-subtle">
            <tr className="type-eyebrow text-text-muted">
              <th className="px-4 py-3">Item</th>
              <th className="px-4 py-3">Pergunta</th>
              <th className="px-4 py-3">Resposta</th>
              <th className="px-4 py-3">Evidências</th>
              <th className="px-4 py-3">Nota para IA</th>
              <th className="px-4 py-3">Veredito da IA</th>
              <th className="px-4 py-3">Obs. do cliente</th>
            </tr>
          </thead>
          {blocos.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={7}>
                  <EstadoVazio titulo="Nenhuma pergunta neste filtro." />
                </td>
              </tr>
            </tbody>
          ) : (
            blocos.map(({ grupo, perguntas: doBloco }) => (
              <tbody key={grupo.id}>
                <tr className="border-border-subtle bg-subtle border-t">
                  <th colSpan={7} scope="rowgroup" className="text-text-primary px-4 py-2 type-sm font-semibold">
                    {grupo.paiId ? rotuloDoGrupo(grupo, grupos) : `Pergunta ${grupo.codigo} › ${grupo.titulo}`}
                  </th>
                </tr>
                {doBloco.map((p) => (
                  <LinhaDoQuestionario
                    key={p.id}
                    idCliente={idCliente}
                    padraoId={padraoId}
                    pergunta={p}
                    item={porPergunta.get(p.id)}
                    sugestoes={sugestoesPendentes.filter((s) => s.perguntaDestino.id === p.id)}
                    evidencias={evidencias}
                  />
                ))}
              </tbody>
            ))
          )}
        </table>
      </div>
    </section>
  )
}

function ChipDeFiltro({
  ativo,
  aoClicar,
  tom = 'neutro',
  children,
}: {
  ativo: boolean
  aoClicar: () => void
  tom?: 'neutro' | 'atencao' | 'perigo'
  children: React.ReactNode
}) {
  const cores = {
    neutro: ativo ? 'bg-action-primary text-action-primary-fg border-action-primary' : 'border-border-subtle bg-surface-card text-text-body',
    atencao: ativo ? 'bg-warning-500 text-text-inverse border-warning-500' : 'border-warning-500 bg-warning-100 text-warning-700',
    perigo: ativo ? 'bg-danger-500 text-text-inverse border-danger-500' : 'border-danger-500 bg-danger-100 text-danger-700',
  }[tom]
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={aoClicar}
      className={`rounded-control inline-flex items-center gap-1.5 border px-3 py-1.5 type-sm duration-fast ${cores}`}
    >
      {children}
    </button>
  )
}

function LinhaDoQuestionario({
  idCliente,
  padraoId,
  pergunta,
  item,
  sugestoes,
  evidencias,
}: {
  idCliente: string
  padraoId: string
  pergunta: Pergunta
  item: ItemQuestionario | undefined
  sugestoes: Sugestao[]
  evidencias: Evidencia[]
}) {
  const podeResponder = usePermission('compliance.questionario').editar
  const podeAvaliar = usePermission('compliance.notas_ia').editar
  const podeEnviar = usePermission('compliance.documentos').editar
  const salvarResposta = useSalvarResposta(idCliente, padraoId)
  const salvarNota = useSalvarNotaParaIA(idCliente, padraoId)
  const [erroDeAbertura, setErroDeAbertura] = useState<string | null>(null)

  const divergente = item?.veredito?.situacao === 'discorda'
  const semEvidencia = (item?.evidencias.length ?? 0) === 0
  const opcaoId = salvarResposta.isPending
    ? (salvarResposta.variables?.opcaoId ?? '')
    : (item?.resposta?.opcaoId ?? '')
  const veredito = item?.veredito ? VEREDITO[item.veredito.situacao] : undefined

  const fundo = divergente
    ? 'bg-danger-100 shadow-[inset_4px_0_0_var(--danger-500)]'
    : semEvidencia
      ? 'bg-warning-100'
      : ''

  async function abrir(idEvidencia: string) {
    setErroDeAbertura(null)
    try {
      const url = await obterUrlDeDownload(idCliente, idEvidencia)
      window.open(url, '_blank', 'noopener')
    } catch (erro) {
      setErroDeAbertura(mensagemDoErro(erro))
    }
  }

  return (
    <tr className={`border-border-subtle border-t align-middle ${fundo}`} data-divergente={divergente || undefined}>
      <td className="text-text-primary px-4 py-3 type-label whitespace-nowrap">{pergunta.codigo}</td>
      <td className="text-text-body px-4 py-3 type-sm">{pergunta.enunciado}</td>
      <td className="px-4 py-3">
        <select
          aria-label={`Resposta ${pergunta.codigo}`}
          value={opcaoId}
          disabled={!podeResponder}
          onChange={(e) =>
            salvarResposta.mutate({
              perguntaId: pergunta.id,
              opcaoId: e.target.value || null,
              observacoes: item?.resposta?.observacoes ?? null,
            })
          }
          className={`rounded-field bg-surface-card type-sm w-32 border px-3 py-2 disabled:bg-gray-50 ${
            divergente ? 'border-danger-500 text-danger-700 font-semibold' : 'border-border-subtle text-text-primary'
          }`}
        >
          <option value="">selecionar</option>
          {pergunta.opcoes.map((o) => (
            <option key={o.id} value={o.id}>
              {o.rotulo}
            </option>
          ))}
        </select>
        {salvarResposta.isError && (
          <p role="alert" className="type-sm text-danger-700 mt-1">
            {mensagemDoErro(salvarResposta.error)}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        {!semEvidencia && (
          <ul className="flex flex-col gap-1">
            {item!.evidencias.map((e) => (
              <li key={e.id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => void abrir(e.id)}
                  className="text-text-link hover:text-iox-teal inline-flex items-center gap-1 text-left type-sm"
                >
                  <i className={`ph ${e.tipo === 'link' ? 'ph-link' : 'ph-file-text'}`} aria-hidden="true" />
                  {e.nome}
                </button>
                {podeEnviar && (
                  <Desvincular
                    idCliente={idCliente}
                    pergunta={pergunta}
                    evidencia={evidencias.find((x) => x.id === e.id)}
                    nome={e.nome}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
        {sugestoes.map((s) => (
          <p key={s.id} className="text-iox-navy flex items-center gap-1 type-sm">
            <i className="ph ph-sparkle text-iox-teal" aria-hidden="true" />
            <span>
              sugestão da IA: <strong>{s.evidencia.nome}</strong>
            </span>
          </p>
        ))}
        {semEvidencia &&
          (podeEnviar ? (
            <Link
              to={`/cliente/${idCliente}/compliance/documentos?padrao=${encodeURIComponent(padraoId)}&requisito=${encodeURIComponent(pergunta.id)}`}
              className="text-text-link hover:text-iox-teal inline-flex items-center gap-1 type-label"
            >
              <i className="ph ph-upload-simple" aria-hidden="true" />
              enviar evidência
            </Link>
          ) : (
            sugestoes.length === 0 && <span className="text-text-muted type-sm">—</span>
          ))}
        {erroDeAbertura && <p className="type-sm text-danger-700 mt-1">{erroDeAbertura}</p>}
      </td>
      <td className="px-4 py-3">
        <select
          aria-label={`Nota para IA ${pergunta.codigo}`}
          value={item?.veredito ? String(item.notaParaIA?.nota ?? '') : ''}
          disabled={!item?.veredito || !podeAvaliar || salvarNota.isPending}
          onChange={(e) =>
            salvarNota.mutate({ perguntaId: pergunta.id, nota: e.target.value ? Number(e.target.value) : null })
          }
          className={`rounded-field bg-surface-card type-sm w-20 border px-3 py-2 disabled:bg-gray-50 ${
            divergente ? 'border-danger-500' : 'border-border-subtle'
          }`}
        >
          <option value="">—</option>
          {NOTAS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        {salvarNota.isError && (
          <p role="alert" className="type-sm text-danger-700 mt-1">
            {mensagemDoErro(salvarNota.error)}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        {sugestoes.map((s) => (
          <DecisaoDaSugestao key={s.id} idCliente={idCliente} sugestao={s} editavel={podeResponder} />
        ))}
        {veredito ? (
          <div className="flex items-start gap-2">
            <Badge tom={veredito.tom}>{veredito.rotulo}</Badge>
            {item?.veredito?.recomendacao && (
              <span className={`type-sm ${divergente ? 'text-danger-700' : 'text-text-body'}`}>
                {item.veredito.recomendacao}
              </span>
            )}
          </div>
        ) : item?.evidencias.some((e) => e.tipo !== 'link') ? (
          <span className="text-text-muted inline-flex items-center gap-1 type-sm">
            <i className="ph ph-hourglass-medium" aria-hidden="true" /> aguardando parecer da IA
          </span>
        ) : (
          sugestoes.length === 0 && <span className="text-text-muted type-sm">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <ObservacaoDoCliente
          key={item?.resposta?.atualizadaEm ?? 'nova'}
          codigo={pergunta.codigo}
          salva={item?.resposta?.observacoes ?? ''}
          editavel={podeResponder}
          salvando={salvarResposta.isPending}
          aoSalvar={(texto) =>
            salvarResposta.mutate({
              perguntaId: pergunta.id,
              opcaoId: item?.resposta?.opcaoId ?? null,
              observacoes: texto.trim() || null,
            })
          }
        />
      </td>
    </tr>
  )
}

function ObservacaoDoCliente({
  codigo,
  salva,
  editavel,
  salvando,
  aoSalvar,
}: {
  codigo: string
  salva: string
  editavel: boolean
  salvando: boolean
  aoSalvar: (texto: string) => void
}) {
  const [texto, setTexto] = useState(salva)
  const [editando, setEditando] = useState(salva === '')

  return (
    <span className="flex items-center gap-2">
      <input
        aria-label={`Observação ${codigo}`}
        placeholder="observação"
        value={texto}
        disabled={!editavel || !editando}
        onChange={(e) => setTexto(e.target.value)}
        className="rounded-field border-border-subtle bg-surface-card text-text-primary type-sm min-w-44 flex-1 border px-3 py-2 disabled:bg-gray-50"
      />
      {editavel && (
        <>
          <button
            type="button"
            aria-label={`salvar observação ${codigo}`}
            disabled={!editando || salvando || texto === salva}
            onClick={() => {
              aoSalvar(texto)
              setEditando(false)
            }}
            className="text-success-700 disabled:opacity-40"
          >
            <i className="ph ph-check" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={`editar observação ${codigo}`}
            disabled={editando}
            onClick={() => setEditando(true)}
            className="text-icon-default hover:text-iox-teal disabled:opacity-40"
          >
            <i className="ph ph-pencil-simple" aria-hidden="true" />
          </button>
        </>
      )}
    </span>
  )
}

const CONFIANCA: Record<Sugestao['confianca'], { rotulo: string; tom: 'sucesso' | 'atencao' | 'neutro' }> = {
  alta: { rotulo: 'confiança alta', tom: 'sucesso' },
  media: { rotulo: 'confiança média', tom: 'atencao' },
  baixa: { rotulo: 'confiança baixa', tom: 'neutro' },
}

/** Aceitar / recusar a sugestão da IA na própria linha — o mesmo PATCH da aba Sugestões. */
function DecisaoDaSugestao({
  idCliente,
  sugestao,
  editavel,
}: {
  idCliente: string
  sugestao: Sugestao
  editavel: boolean
}) {
  const decidir = useDecidirSugestao(idCliente)
  const alvo = `${sugestao.evidencia.nome} → ${sugestao.perguntaDestino.codigo}`
  return (
    <div className="mb-2 flex flex-col gap-2">
      <span className="flex items-center gap-2">
        <Badge tom={CONFIANCA[sugestao.confianca].tom}>{CONFIANCA[sugestao.confianca].rotulo}</Badge>
      </span>
      {editavel && (
        <span className="flex gap-2">
          <Botao
            variante="primaria"
            aria-label={`aceitar sugestão ${alvo}`}
            disabled={decidir.isPending}
            title="aceitar sugestão"
            className="px-2"
            onClick={() => decidir.mutate({ id: sugestao.id, estado: 'aceita' })}
          >
            <i className="ph ph-check" aria-hidden="true" />
          </Botao>
          <Botao
            aria-label={`recusar sugestão ${alvo}`}
            disabled={decidir.isPending}
            title="recusar sugestão"
            className="px-2"
            onClick={() => decidir.mutate({ id: sugestao.id, estado: 'descartada' })}
          >
            <i className="ph ph-x" aria-hidden="true" />
          </Botao>
        </span>
      )}
      {decidir.isError && (
        <p role="alert" className="type-sm text-danger-700">
          {mensagemDoErro(decidir.error)}
        </p>
      )}
    </div>
  )
}

/**
 * O "×" de uma evidência: tira ESTA pergunta dos vínculos do documento. O backend não
 * aceita documento sem vínculo — se era o último, pede confirmação para excluir o
 * documento em vez de desvincular.
 */
function Desvincular({
  idCliente,
  pergunta,
  evidencia,
  nome,
}: {
  idCliente: string
  pergunta: Pergunta
  evidencia: Evidencia | undefined
  nome: string
}) {
  const atualizar = useAtualizarVinculos(idCliente)
  const excluir = useExcluirEvidencia(idCliente)
  const [confirmando, setConfirmando] = useState(false)
  const restantes = (evidencia?.vinculos ?? [])
    .filter((v) => v.perguntaId !== pergunta.id)
    .map((v) => ({ padraoId: v.padraoId, perguntaId: v.perguntaId }))
  const ocupado = atualizar.isPending || excluir.isPending

  if (confirmando) {
    return (
      <span className="flex flex-wrap items-center gap-2 type-sm">
        <span className="text-text-body">Único requisito deste documento. Excluir o documento?</span>
        <Botao
          variante="primaria"
          aria-label={`confirmar exclusão de ${nome}`}
          disabled={ocupado}
          onClick={() => evidencia && excluir.mutate(evidencia.id)}
        >
          excluir
        </Botao>
        <Botao aria-label={`cancelar exclusão de ${nome}`} onClick={() => setConfirmando(false)}>
          cancelar
        </Botao>
        {excluir.isError && (
          <span role="alert" className="text-danger-700">
            {mensagemDoErro(excluir.error)}
          </span>
        )}
      </span>
    )
  }

  return (
    <>
      <button
        type="button"
        aria-label={`remover ${nome} de ${pergunta.codigo}`}
        title="remover evidência deste item"
        // Sem o vínculo completo (lista de documentos carregando) não dá para saber o que sobra.
        disabled={!evidencia || ocupado}
        onClick={() =>
          restantes.length > 0 ? atualizar.mutate({ id: evidencia!.id, vinculos: restantes }) : setConfirmando(true)
        }
        className="text-icon-default hover:text-danger-700 rounded-control px-1 disabled:opacity-40"
      >
        <i className="ph ph-x" aria-hidden="true" />
      </button>
      {atualizar.isError && (
        <span role="alert" className="type-sm text-danger-700">
          {mensagemDoErro(atualizar.error)}
        </span>
      )}
    </>
  )
}
